import { useRef, useState } from "react";

import { UiUploadIcon } from "@jti/brand-icons/ui-upload";

import { Button } from "../Button";
import { FormFieldLabel } from "../FormFieldLabel";
import { SupportingText } from "../SupportingText";
import { Tag } from "../Tag";

import styles from "./FilePicker.module.scss";

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export type FilePickerProps = {
  id?: string;
  label?: string;
  helperText?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  errorMessages?: {
    unsupportedType?: string;
    maxSize?: string;
    maxFilesReached?: string;
  };
  isRequired?: boolean;
  isListItemVisible?: boolean;
  maxFiles?: number;
  maxSize?: number;
  acceptedFileTypes?: string[];
  buttonLabel?: string;
  value?: File[];
  onChange?: (files: File[]) => void;
  dataTestId?: string;
  optionalText?: string;
  dismissAriaLabel?: string;
};

export const FilePicker = ({
  id = "filePicker",
  label = "Have something to upload?",
  helperText = "Supported formats: JPEG, PNG, PDF, DOCX. Max size: XXX MB.",
  isInvalid = false,
  errorMessage = "Please upload a valid file.",
  errorMessages,
  isRequired = false,
  isListItemVisible,
  maxFiles = 3,
  maxSize = 5 * 1024 * 1024,
  acceptedFileTypes = ["image/jpeg", "image/png", "application/pdf", "application/msword"],
  buttonLabel = "Browse files",
  value = [],
  onChange,
  dataTestId = "filePicker",
  optionalText = "(optional)",
  dismissAriaLabel = "Remove file",
}: FilePickerProps) => {
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    let errorMsg: string | undefined = undefined;

    const validFiles = selectedFiles.filter((file) => {
      if (!acceptedFileTypes.includes(file.type)) {
        errorMsg = errorMessages?.unsupportedType ?? "Unsupported file type.";
        return false;
      }
      if (file.size > maxSize) {
        errorMsg = errorMessages?.maxSize ?? "File size exceeds the maximum allowed.";
        return false;
      }
      return true;
    });

    if (value.length + validFiles.length > maxFiles) {
      setFileError(errorMessages?.maxFilesReached ?? "Maximum number of allowable file uploads have been exceeded.");
      return;
    }

    setFileError(errorMsg);
    onChange?.([...value, ...validFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFileError(undefined);
    onChange?.(value.filter((_, i) => i !== index));
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayErrorText = isInvalid && !!errorMessage ? errorMessage : fileError;
  const hasError = (isInvalid && !!errorMessage) || !!fileError;
  const showFileList = isListItemVisible ?? value.length > 0;

  return (
    <div className={styles.filePickerContainer} data-testid={`${dataTestId}-container`}>
      <FormFieldLabel
        label={label}
        htmlFor={id}
        necessityIndicator
        required={isRequired}
        requiredText="*"
        optionalText={optionalText}
      />
      <div className={styles.filePickerWrapper}>
        <div className={styles.inputContainer}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={acceptedFileTypes.join(",")}
            multiple={maxFiles > 1}
            hidden
            name={id}
            id={id}
            data-testid={dataTestId}
          />
          <Button
            className={styles.uploadButton}
            variant="secondary"
            size="md"
            onClick={handleClick}
            disabled={value.length >= maxFiles}
            iconAfter={UiUploadIcon}
          >
            {buttonLabel}
          </Button>
        </div>
      </div>
      {helperText && !hasError && <SupportingText variant="info">{helperText}</SupportingText>}
      {hasError && displayErrorText && <SupportingText variant="error">{displayErrorText}</SupportingText>}
      {showFileList && (
        <div className={styles.fileList} data-testid="fileList">
          {value.map((file, index) => (
            <Tag
              key={file.name}
              label={file.name}
              description={`(${formatFileSize(file.size)})`}
              onDismiss={() => handleRemoveFile(index)}
              dismissAriaLabel={dismissAriaLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
};
